// src/routes.config.js
import { lazy } from "react";
import { createBrowserRouter } from "react-router";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import MapPage from "./screens/MapPage/MapPage";
import Order from "./screens/Order";
import RouteErrorBoundary from "./components/ui/boundaries/routeErrorBoundary";
import BridgeRouteErrorBoundary from "./components/ui/boundaries/bridge";


// Root layout component
const RootLayout = lazy(() => import("./layouts/RootLayout"));

// Lazy load components for better performance
const GridPage = lazy(() => import("./screens/GridPage/GridPage"));

const Dashboard = lazy(() => import("./screens/Dashboard/Dashboard"));
const Signup = lazy(() => import("./screens/Auth/Signin"));
const NotFound = lazy(() => import("./screens/NotFound/NotFound"));
const Profile = lazy(() => import("./screens/Profile/Profile"));
const MagicLogin = lazy(() => import("./screens/Auth/MagicLogin"));
const LocationV2 = lazy(() => import("./screens/Location/V2"));
const ReservePay = lazy(() => import("./screens/ReservePay/ReservePay"));
const StoreDetails = lazy(() => import("./screens/StoreDetails/StoreDetails"));
const Cart = lazy(() => import("./screens/Cart/Cart"));
const ResetPasswordConfirm = lazy(() => import("./screens/Auth/ResetPasswordConfirm"));
const PrivacyPolicy = lazy(() => import("./screens/Privacy/PrivacyPolicy"));
const Root = lazy(() => import("./screens/Root/Root"));
const StripeSuccess = lazy(
  () => import("./screens/StripeSuccess/StripeSuccess"),
);
const StripeCancelled = lazy(
  () => import("./screens/StripeCancelled/StripeCancelled"),
);

// Route definitions
export const routes = [
  {
    path: "/",
    element: Root,
    title: "HomePage",
    isPublic: true
  },
  {
    path: "/signin",
    element: Signup,
    title: "Sign Up",
    isPublic: true,
  },
  {
    path: "/reset-password",
    element: ResetPasswordConfirm,
    title: "Reset Password",
    isPublic: true,
  },
  {
    path: "/location",
    element: LocationV2,
    title: "Set Location",
    isPublic: true,
  },
  {
    path: "/magic-login",
    element: MagicLogin,
    title: "Magic Login",
    isPublic: true,
  },
  {
    path: "/dashboard",
    element: Dashboard,
    title: "Dashboard",
    isPublic: true,
  },
  {
    path: "/profile",
    element: Profile,
    title: "Profile",
    isPublic: false,
  },
  {
    path: "/store/:storeId",
    element: StoreDetails,
    title: "Store Details",
    isPublic: true
  },
  {
    path: "/order-summary/:storeId",
    element: Cart,
    title: "Order Summary",
    isPublic: true, // 
  },
  {
    path: "/order-success",
    element: StripeSuccess,
    title: "Payment Success",
    isPublic: false, // Protect success page
  },
  {
    path: "/order-cancelled",
    element: StripeCancelled,
    title: "Payment Cancelled",
    isPublic: false, // Protect cancelled page
  },
  {
    path: "/reservepay/:bagId?",
    element: ReservePay,
    title: "Reserve & Pay",
    isPublic: false,
  },
  {
    path: "/favorites/",
    element: GridPage,
    title: "Favorites",
    isPublic: true,
  },
  {
    path: "/category/:taxonomy-slug",
    element: GridPage,
    title: "Taxonomy",
    isPublic: false,
  },
  {
    path: "/map/",
    element: MapPage,
    title: "Map",
    isPublic: true,
  },
  {
    path: "*",
    element: NotFound,
    title: "Not Found",
    isPublic: true,
  },
  {
    path: "/order/:publicOrderId",
    element: Order,
    title: "Order",
    isPublic: true,
  },

  {
    path: "/privacy-policy",
    element: PrivacyPolicy,
    title: "Privacy Policy",
    isPublic: true,
  }

];

// Create and export the router configuration
export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <BridgeRouteErrorBoundary />,
    children: routes.map(({ path, element: Element, isPublic }) => ({
      path: path === "/" ? "" : path, // Empty string for root path
      element: isPublic ? (
        <Element />
      ) : (
        <ProtectedRoute>
          <Element />
        </ProtectedRoute>
      ),
      errorElement: <RouteErrorBoundary />,
    })),
  },
]);
